// src/pages/Practice.jsx
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { HiArrowRight, HiAcademicCap, HiBookOpen, HiLightningBolt, HiDocumentText } from "react-icons/hi";
import { db } from "../components/firebase/firebase";
import { collection, getDocs } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { SEO } from "../components/common/SEO";

// Real <a href> links (not onClick) so search engines can follow them.
const MotionLink = motion(Link);

const KNOWN_BOARDS = ["state", "cbse", "competitive"];

const BOARD_META = [
  {
    id: "state",
    name: "State Board",
    desc: "Tamil Nadu State Board",
    icon: <HiAcademicCap className="text-xl text-blue-600" />,
    iconBg: "bg-blue-50",
    accent: "text-blue-600",
    border: "border-blue-200",
    tag: "bg-blue-600",
  },
  {
    id: "cbse",
    name: "CBSE",
    desc: "Central Board of Secondary Education",
    icon: <HiBookOpen className="text-xl text-indigo-600" />,
    iconBg: "bg-indigo-50",
    accent: "text-indigo-600",
    border: "border-indigo-200",
    tag: "bg-indigo-600",
  },
  {
    id: "competitive",
    name: "Competitive",
    desc: "JEE · NEET · TNPSC & More",
    icon: <HiLightningBolt className="text-xl text-sky-600" />,
    iconBg: "bg-sky-50",
    accent: "text-sky-600",
    border: "border-sky-200",
    tag: "bg-sky-600",
  },
];

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };
const fadeUp  = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } } };

const sortByOrder = (arr) => [...arr].sort((a, b) => (a.order || 0) - (b.order || 0));

export function Practice() {
  const { board: boardParam, grade, examKey } = useParams();
  const navigate = useNavigate();
  const { role, profile } = useAuth();

  // Derive actual board and grade label from URL segments.
  // 2-param URLs (/practice/state/10): grade=board, examKey=gradeLabel
  // 3-param URLs (/practice/state/10/sub): boardParam=board, grade=gradeLabel
  const BOARD       = boardParam || (KNOWN_BOARDS.includes(grade) ? grade : "state");
  const GRADE_LABEL = boardParam ? grade : (KNOWN_BOARDS.includes(grade) ? examKey : grade);

  // Landing state
  const [allGrades, setAllGrades] = useState({ state: [], cbse: [], competitive: [] });
  const [landingLoading, setLandingLoading] = useState(true);

  // Nested view state
  const [gradesData, setGradesData]     = useState([]);
  const [subjectsData, setSubjectsData] = useState([]);

  // ── Landing: fetch all boards' grades in parallel ───────────────────
  useEffect(() => {
    if (grade) return;
    Promise.all(
      KNOWN_BOARDS.map((b) =>
        getDocs(collection(db, "curriculum", b, "grades")).then((snap) =>
          sortByOrder(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
        )
      )
    )
      .then(([state, cbse, competitive]) => setAllGrades({ state, cbse, competitive }))
      .catch(console.error)
      .finally(() => setLandingLoading(false));
  }, [grade]);

  // ── VIEW 2: fetch grades for board when only board is in URL ─────────
  useEffect(() => {
    if (!grade || examKey) return;
    getDocs(collection(db, "curriculum", BOARD, "grades"))
      .then((snap) => setGradesData(sortByOrder(snap.docs.map((d) => ({ id: d.id, ...d.data() })))))
      .catch(console.error);
  }, [grade, examKey, BOARD]);

  // ── VIEW 3: fetch subjects directly (no exam layer) ──────────────────
  useEffect(() => {
    if (!grade || !examKey) return;
    setSubjectsData([]);
    getDocs(collection(db, "curriculum", BOARD, "grades"))
      .then((snap) => {
        const all = sortByOrder(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        const gradeDoc = all.find((g) => g.label === GRADE_LABEL);
        if (!gradeDoc) return null;
        return getDocs(collection(db, "curriculum", BOARD, "grades", gradeDoc.id, "subjects"));
      })
      .then((snap) => {
        if (!snap) return;
        setSubjectsData(sortByOrder(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
      })
      .catch(console.error);
  }, [grade, examKey, BOARD, GRADE_LABEL]);

  // ── Student shortcut: skip the board + class picker ──────────────────
  // If a student lands on the landing or board view, jump them directly
  // to their assigned board+grade subject list.
  useEffect(() => {
    if (role !== "student" || !profile?.board || !profile?.grade) return;
    // Only redirect when they haven't already landed on their own grade page.
    const alreadyThere = BOARD === profile.board && String(GRADE_LABEL) === String(profile.grade);
    if (!alreadyThere) {
      navigate(`/practice/${profile.board}/${profile.grade}`, { replace: true });
    }
  }, [role, profile, BOARD, GRADE_LABEL, navigate]);

  // ══════════════════════════════════════════════════════════════════════
  // VIEW 1 — Landing: all boards + their classes
  // ══════════════════════════════════════════════════════════════════════
  if (!grade) {
    return (
      <div className="min-h-screen bg-white">
        <SEO
          title="Question Papers by Board & Class — State Board, CBSE, JEE, NEET, TNPSC"
          description="Browse free question papers by board and class: Tamil Nadu State Board, CBSE and competitive exams (JEE, NEET, TNPSC). All subjects, view online."
          canonical="/practice"
        />
        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white py-14 px-6">
          <div className="mx-auto max-w-screen-lg">
            <motion.p initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
              className="text-blue-200 text-xs font-semibold tracking-widest uppercase mb-3">
              Study Materials
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="text-3xl sm:text-4xl font-bold text-white">
              Browse by Board &amp; Class
            </motion.h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
              className="text-blue-200 text-sm mt-2">
              Choose a class under your board to find papers.
            </motion.p>
          </div>
        </div>

        <div className="mx-auto max-w-screen-lg px-6 py-12 space-y-12">
          {landingLoading ? (
            <p className="text-center text-gray-400 text-sm py-16">Loading...</p>
          ) : (
            BOARD_META.map((board, bi) => (
              <motion.div key={board.id} variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <motion.div variants={fadeUp} className="flex items-center gap-3 mb-5">
                  <div className={`w-9 h-9 rounded-xl ${board.iconBg} flex items-center justify-center`}>
                    {board.icon}
                  </div>
                  <div>
                    <h2 className={`text-lg font-bold ${board.accent}`}>{board.name}</h2>
                    <p className="text-xs text-gray-400">{board.desc}</p>
                  </div>
                </motion.div>

                {allGrades[board.id].length === 0 ? (
                  <motion.p variants={fadeUp} className="text-sm text-gray-400 pl-12">No classes available yet.</motion.p>
                ) : (
                  <div className="flex flex-wrap gap-3 pl-12">
                    {allGrades[board.id].map((g) => (
                      <MotionLink
                        key={g.id}
                        variants={fadeUp}
                        to={`/practice/${board.id}/${g.label}`}
                        whileHover={{ y: -2, scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        className={`group border-2 ${board.border} hover:bg-blue-50 bg-white rounded-xl px-5 py-3 flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-blue-700 transition-all duration-200 shadow-sm hover:shadow-md`}
                      >
                        <span className={`w-2 h-2 rounded-full ${board.tag}`} />
                        {g.name}
                        <HiArrowRight className={`${board.accent} text-sm opacity-0 group-hover:opacity-100 transition-opacity`} />
                      </MotionLink>
                    ))}
                  </div>
                )}

                {bi < BOARD_META.length - 1 && <div className="mt-12 border-t border-dashed border-gray-200" />}
              </motion.div>
            ))
          )}
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // VIEW 2 — Board selected, no grade yet → show classes for that board
  // ══════════════════════════════════════════════════════════════════════
  if (grade && !examKey) {
    const boardMeta = BOARD_META.find((b) => b.id === BOARD) || BOARD_META[0];
    return (
      <div className="min-h-screen bg-white">
        <SEO
          title={`${boardMeta.name} Question Papers — All Classes`}
          description={`Free ${boardMeta.desc} question papers for every class and subject. Choose your class to view papers online.`}
          canonical={`/practice/${BOARD}`}
        />
        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white py-14 px-6">
          <div className="mx-auto max-w-screen-lg">
            <button onClick={() => navigate(-1)}
              className="text-blue-200 hover:text-white text-sm font-medium mb-4 flex items-center gap-1 transition-colors">
              ← Back
            </button>
            <span className={`inline-block ${boardMeta.tag} text-white text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-3`}>
              {boardMeta.name}
            </span>
            <h1 className="text-3xl sm:text-4xl font-bold text-white">Select Your Class</h1>
          </div>
        </div>

        <div className="mx-auto max-w-screen-lg px-6 py-12">
          {gradesData.length === 0 ? (
            <p className="text-center text-gray-400 text-sm py-16">No classes available for this board.</p>
          ) : (
            <motion.div variants={stagger} initial="hidden" animate="visible"
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {gradesData.map((g) => (
                <MotionLink
                  key={g.id}
                  variants={fadeUp}
                  to={`/practice/${BOARD}/${g.label}`}
                  whileHover={{ y: -4 }}
                  whileTap={{ scale: 0.98 }}
                  className="group cursor-pointer bg-white border-2 border-gray-100 hover:border-blue-300 rounded-2xl p-6 flex items-center justify-between shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <HiAcademicCap className="text-blue-600 text-xl" />
                    </div>
                    <span className="text-base font-semibold text-gray-800">{g.name}</span>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-blue-600 group-hover:bg-blue-700 flex items-center justify-center flex-shrink-0 transition-colors">
                    <HiArrowRight className="text-white text-sm" />
                  </div>
                </MotionLink>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // VIEW 3 — Board + grade selected → show subjects directly
  // ══════════════════════════════════════════════════════════════════════
  const boardMeta = BOARD_META.find((b) => b.id === BOARD) || BOARD_META[0];
  const classTitle = BOARD === "competitive"
    ? `${String(GRADE_LABEL).toUpperCase()} Question Papers`
    : `Class ${GRADE_LABEL} ${boardMeta.name} Question Papers`;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${classTitle} — All Subjects`}
        description={`Free ${classTitle.replace(" Question Papers", "")} question papers for all subjects — view online. ${boardMeta.desc}.`}
        canonical={`/practice/${BOARD}/${GRADE_LABEL}`}
      />
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white py-14 px-6">
        <div className="mx-auto max-w-screen-lg">
          <button onClick={() => navigate(-1)}
            className="text-blue-200 hover:text-white text-sm font-medium mb-4 flex items-center gap-1 transition-colors">
            ← Back
          </button>
          <span className={`inline-block ${boardMeta.tag} text-white text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-3`}>
            {boardMeta.name} · Class {GRADE_LABEL}
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold text-white">Select a Subject</h1>
          <p className="text-blue-200 text-sm mt-2">Choose a subject to view papers</p>
        </div>
      </div>

      <div className="mx-auto max-w-screen-lg px-6 py-12">
        {subjectsData.length === 0 ? (
          <p className="text-center text-gray-400 text-sm py-16">No subjects available for this class.</p>
        ) : (
          <motion.div variants={stagger} initial="hidden" animate="visible"
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {subjectsData.map((sub) => (
              <motion.div key={sub.id} variants={fadeUp} whileHover={{ y: -4 }}>
                <Link to={`/resources/${BOARD}/${GRADE_LABEL}/${sub.key}`}>
                  <div className="group bg-white border-2 border-gray-100 hover:border-blue-300 rounded-2xl p-6 text-center cursor-pointer hover:shadow-md transition-all duration-200">
                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
                      <HiDocumentText className="text-blue-600 text-xl" />
                    </div>
                    <span className="text-sm font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">
                      {sub.name}
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default Practice;
